import React from "react";
import * as Icon from "react-feather";
import { NOTE_TEMPLATES } from "../../shared/helpers/constants";
import "../../afs-walkthrough.css";

function generateBasisNote({ compilationData, entityName, yearEnd, balanceSheet }) {
	let entity = entityName || "[Entity Name]";
	let yearEndLabel = yearEnd ? new Date(yearEnd + "T00:00:00").toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "[Year End]";

	let intro = "The basis of accounting applied in the preparation of the balance sheet of " + entity + " as at " + yearEndLabel + ", and the statements of retained earnings and income for the year then ended is the historical cost basis and reflects cash transactions with the addition of:";

	let additions = [];

	if (!balanceSheet || !balanceSheet.sections) {
		additions.push("accounts receivable less an allowance for doubtful accounts");
		additions.push("property, plant and equipment amortized in accordance with amounts allowable for income tax purposes");
		additions.push("accounts payable and accrued liabilities");
		additions.push("current income taxes payable as at the reporting date");
		return intro + "\n\n" + additions.map(function (a) { return "\u2022 " + a; }).join("\n");
	}

	let sectionNames = new Set();
	for (let section of balanceSheet.sections) {
		let hasPrior = section.totalPrior !== 0;
		let hasCurrent = section.totalCurrent !== 0;
		if (hasPrior || hasCurrent) {
			sectionNames.add(section.name.toLowerCase());
		}
	}

	if (sectionNames.has("accts. rec., trade & other") || sectionNames.has("accounts receivable")) {
		additions.push("accounts receivable less an allowance for doubtful accounts");
	}

	for (let name of sectionNames) {
		if (name.includes("investment") && !name.includes("long term")) {
			additions.push("investments recorded at cost");
			break;
		}
	}

	if (sectionNames.has("goodwill")) {
		additions.push("goodwill recorded at cost");
	}

	for (let name of sectionNames) {
		if (name.includes("fixed asset") || name.includes("property") || name.includes("equipment")) {
			additions.push("property, plant and equipment amortized in accordance with amounts allowable for income tax purposes");
			break;
		}
	}

	if (sectionNames.has("inventory")) {
		additions.push("inventory at the lower of cost and net realizable value");
	}

	for (let name of sectionNames) {
		if (name.includes("accounts payable") || name.includes("acc. liab")) {
			additions.push("accounts payable and accrued liabilities");
			break;
		}
	}

	for (let name of sectionNames) {
		if (name.includes("income tax")) {
			additions.push("current income taxes payable as at the reporting date");
			break;
		}
	}

	if (additions.length === 0) {
		additions.push("accounts payable and accrued liabilities");
		additions.push("current income taxes payable as at the reporting date");
	}

	return intro + "\n\n" + additions.map(function (a) { return "\u2022 " + a; }).join("\n");
}

function NoteView({ noteId, compilationData, entityName, yearEnd, balanceSheet, noteTexts, onNoteTextChange, isCustom, title, onTitleChange, onRemove }) {
	let template = NOTE_TEMPLATES[noteId];
	if (!template && !isCustom) return null;

	let text = noteTexts[noteId];

	// Initialize a standard note's text from its template if not yet set. Custom notes start empty.
	if (text === undefined) {
		if (isCustom) {
			text = "";
		} else if (noteId === "n_basis") {
			text = generateBasisNote({ compilationData, entityName, yearEnd, balanceSheet });
		} else {
			text = template.template(compilationData);
		}
		if (!isCustom && onNoteTextChange) {
			setTimeout(function () { onNoteTextChange(noteId, text); }, 0);
		}
	}

	return (
		<div className="afs-note__card">
			{isCustom ? (
				<div className="afs-note__custom-head">
					<input
						className="afs-note__title-input"
						value={title || ""}
						placeholder="Note title (e.g. Economic Dependence)"
						onChange={function (e) { if (onTitleChange) onTitleChange(e.target.value); }}
					/>
					<button type="button" className="afs-note__remove" title="Remove note" onClick={function () { if (onRemove) onRemove(); }}>
						<Icon.X size={15} />
					</button>
				</div>
			) : (
				<div className="afs-note__ref">{template.ref}</div>
			)}
			<textarea
				className="afs-note__textarea"
				value={text || ""}
				placeholder={isCustom ? "Type the note text…" : undefined}
				onChange={function (e) {
					if (onNoteTextChange) onNoteTextChange(noteId, e.target.value);
				}}
				spellCheck={false}
			/>
			<p className="afs-note__hint">Edit the note text above as needed before downloading the financial information.</p>
		</div>
	);
}

export default NoteView;
